import { useRef, useState, useEffect, type RefObject, useMemo } from 'react'
import useResizeObserver from 'use-resize-observer'

interface CalcResult {
  vertical: boolean
  horizontal: boolean
}

interface UseHasScrollbarsArgs {
  skip?: boolean
}

/**
 * Tracks the resize of a ref element and calculates if vertical and horizontal scrollbars are visible.
 * @template T - the type of the HTML element to track
 * @param {UseHasScrollbarsArgs} [options] - options for the hook
 * @param {boolean} [options.skip=false] - a boolean that indicates whether to skip the tracking of the element's size
 * @returns {[RefObject<T | null>, CalcResult]} - a tuple with a ref of the tracked element and object containing calculation
 */
export const useHasScrollbars = <T extends HTMLElement>({ skip }: UseHasScrollbarsArgs = {}): [
  RefObject<T | null>,
  CalcResult,
] => {
  const [nodeEl, setNodeEl] = useState<T>()
  const [hasVertical, setHasVertical] = useState(false)
  const [hasHorizontal, setHasHorizontal] = useState(false)

  const ref = useRef<T>(null)

  useEffect(() => {
    if (ref.current) {
      setNodeEl(ref.current)
    }
    // eslint-disable-next-line react-hooks/refs, react-hooks/exhaustive-deps -- legacy, see STL-5833 and STL-5832
  }, [ref.current])

  const { width: nodeWidth = 0, height: nodeHeight = 0 } = useResizeObserver<T>({
    ref: skip ? null : nodeEl,
    box: 'border-box',
  })

  useEffect(() => {
    if (!skip) {
      const { scrollHeight = 0, scrollWidth = 0, clientHeight = nodeHeight, clientWidth = nodeWidth } = nodeEl || {}

      // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy, see STL-5836
      setHasVertical(scrollHeight > clientHeight)
      setHasHorizontal(scrollWidth > clientWidth)
    }
  }, [skip, nodeEl, nodeHeight, nodeWidth])

  return useMemo(() => [ref, { vertical: hasVertical, horizontal: hasHorizontal }], [ref, hasHorizontal, hasVertical])
}
